import { FieldProps } from "ra-ui-materialui";
import React, {
  cloneElement,
  InputHTMLAttributes,
  isValidElement,
  useContext,
} from "react";
import { useEffect } from "react";
import { ProtectedFieldLevel } from "../../../common/data/service-proxies";
import { camelCaseToPascalCase } from "../../../common/util/changeCase";
import { FieldPermissionsContext } from "./FieldPermissionsContext";

type ValidateField = ((
  value: any,
  values: any
) =>
  | string
  | undefined
  | {
      message: string;
      args: any;
    }) & {
  isRequired?: boolean;
};

interface Props extends FieldProps, InputHTMLAttributes<HTMLInputElement> {
  source: string;
  children?: React.ReactElement<Props>;
  validate?: ValidateField;
  render?: (
    props: FieldProps & InputHTMLAttributes<HTMLInputElement>
  ) => React.ReactElement<Props>;
}

export const ProtectedElement = ({ children, render, ...props }: Props) => {
  useEffect(() => {
    if (!render && !children) {
      throw new Error("Either children or render need to be specified");
    }
  }, [render, children]);
  const context = useContext(FieldPermissionsContext);
  const child = children ? React.Children.only(children) : render!(props);
  const source = camelCaseToPascalCase(props.source);
  const permission = context.fieldPermissions?.find((x) => x.name === source);
  if (!permission) {
    return isValidElement(child) ? cloneElement(child, props) : null;
  }

  switch (permission.fieldAccessLevel) {
    case ProtectedFieldLevel.Full:
      return isValidElement(child) ? cloneElement(child, props) : null;
    case ProtectedFieldLevel.ReadOnly:
      return isValidElement(child)
        ? cloneElement(child, { ...props, disabled: true })
        : null;
    case ProtectedFieldLevel.Hidden:
      return null;
    default:
      return isValidElement(child) ? cloneElement(child, props) : null;
  }
};
